import { createContext, useContext, useState } from "react";
import { apiPost } from "../services/Api/apiCall";
import { Call, Device, TwilioError } from "@twilio/voice-sdk";
import { translate } from "../services/Translations/translatorService";
import { useCase } from "./CaseProvider";
import { Activity } from "../types/Activity";
import ApiClient from "../services/ApiClient";
import { CalendlyEvent } from "../types/Calendly/CalendlyEvent";
import { enqueueSnackbar } from "notistack";

type CallEndCallback = () => void;

type CallContextType = {
  status: string;
  phoneError: string;
  makeCall: (phoneNumber: string) => void;
  hangUp: () => void;
  cancelRecording: () => Promise<void>;
  recordingEnabled: boolean;
  setRecordingEnabled: (enabled: boolean) => void;
  call?: Call | null;
  device?: Device | null;
  callModalOpen: boolean;
  openCallModal: (activity: Activity | null, callback?: CallEndCallback) => Promise<void>;
  closeCallModal: () => void;
  activity?: Activity | null;
  muted: boolean;
  mute: () => void;
  unmute: () => void;
  toggleMute: () => void;
};

const CallContext = createContext<CallContextType | null>(null);

export const CallProvider = ({ children }: { children: React.ReactNode }) => {
  const [device, setDevice] = useState<Device | null>(null);
  const [recordingEnabled, setRecordingEnabled] = useState<boolean>(false);
  const [callModalOpen, setCallModalOpen] = useState<boolean>(false);
  const [status, setStatus] = useState(translate("call.callStatus.initializing"));
  const [call, setCall] = useState<Call | null>(null);
  const [activity, setActivity] = useState<Activity | null>(null);
  const [calendlyEvent, setCalendlyEvent] = useState<CalendlyEvent | null>(null);
  const [phoneError, setPhoneError] = useState("");
  const [onCallEnd, setOnCallEnd] = useState<CallEndCallback | null>(null);
  const [muted, setMuted] = useState<boolean>(false);
  const { product } = useCase();

  const applyMute = (shouldMute: boolean) => {
    if (!call) return;

    if (call.status() !== "open") {
      call.once("accept", () => applyMute(shouldMute));
      return;
    }

    call.mute(shouldMute);

    const stream = (call as any).getLocalStream?.();
    if (stream) {
      stream.getAudioTracks().forEach((t: { enabled: boolean }) => (t.enabled = !shouldMute));
    }

    setMuted(call.isMuted());
  };

  const mute = () => applyMute(true);
  const unmute = () => applyMute(false);
  const toggleMute = () => {
    if (!call) return;
    applyMute(!call.isMuted());
  };

  const createTwilioDevice = async () => {
    if (device) {
      return;
    }

    const token = await handleTokenFetch();

    if (token) {
      const newDevice = new Device(token, {
        logLevel: "error",
      });

      newDevice.on("ready", () => {
        setStatus(translate("call.callStatus.ready"));
      });

      newDevice.on("error", (deviceError: TwilioError.TwilioError) => {
        setPhoneError(deviceError.message);
      });

      newDevice.on("incoming", (connection) => {
        connection.accept();
        setCall(connection);
        setStatus(translate("call.callStatus.inCallWithInboundCaller"));

        connection.on("accept", () => setMuted(connection.isMuted()));
        connection.on("mute", (isMuted: boolean) => setMuted(isMuted));
        setMuted(connection.isMuted());
      });

      newDevice.on("disconnect", () => {
        setCall(null);
        setMuted(false);
        setStatus(translate("call.callStatus.ended"));
        if (onCallEnd) {
          onCallEnd();
          setOnCallEnd(null);
        }
      });

      setDevice(newDevice);
    }
  };

  const openCallModal = async (activity: Activity | null, callback?: CallEndCallback) => {
    if (!call) {
      await createTwilioDevice();

      if (activity && activity.calendlyEvent) {
        const calendlyEvent = await ApiClient.get(activity.calendlyEvent);

        setCalendlyEvent(calendlyEvent);

        setActivity(activity);
      }

      if (callback) {
        setOnCallEnd(() => callback);
      }
    }

    setCallModalOpen(true);
  };

  const closeCallModal = () => {
    if (!call) {
      setActivity(null);
      setCalendlyEvent(null);
    }

    setCallModalOpen(false);
  };

  const handleTokenFetch = async () => {
    try {
      const response = await apiPost("/twilio/get_token", {});
      return response.token;
    } catch (err: unknown) {
      if (err instanceof Error) {
        setPhoneError(`Failed to fetch token: ${err.message}`);
      } else {
        setPhoneError("Failed to fetch token: Unbekannter Fehler");
      }
    }

    return false;
  };

  const makeCall = async (phoneNumber: string) => {
    if (!device || !product) return;

    const params: Device.ConnectOptions["params"] = {
      To: phoneNumber,
      caseId: product.id.toString(),
      lbProductClass: product.backofficeCase.lbProductClass,
      recordingEnabled: recordingEnabled.toString(),
    };

    if (calendlyEvent) {
      params["calendlyEventId"] = calendlyEvent.id.toString();

      device.connect({ params }).then((call: Call) => {
        call.on("disconnect", () => {
          setCall(null);
          setMuted(false);
          setStatus(translate("call.callStatus.ended"));
          if (onCallEnd) {
            onCallEnd();
            setOnCallEnd(null);
          }
        });

        call.on("mute", (isMuted: boolean) => setMuted(isMuted));
        setMuted(call.isMuted());

        setCall(call);
        setStatus(`Wähle ${phoneNumber}...`);
      });
    } else {
      enqueueSnackbar(
        "Calendly ID nicht gefunden. Bitte Call Dialog erneut öffnen ggf. Fallseite neu laden. Wenn das Problem weiter besteht die IT informieren.",
        {
          variant: "custom",
          isNonInteractive: true,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        }
      );
    }
  };

  const hangUp = () => {
    if (call) {
      call.disconnect();
    }
  };

  const cancelRecording = async () => {
    await apiPost("/twilio/cancel_recording", {
      calendlyEventId: calendlyEvent?.id,
    });

    setRecordingEnabled(false);
  };

  return (
    <CallContext.Provider
      value={{
        status,
        phoneError,
        makeCall,
        hangUp,
        cancelRecording,
        recordingEnabled,
        setRecordingEnabled,
        call,
        device,
        callModalOpen,
        openCallModal,
        closeCallModal,
        activity,
        muted,
        mute,
        unmute,
        toggleMute,
      }}
    >
      {children}
    </CallContext.Provider>
  );
};

export const useOptionalCall = () => useContext(CallContext);

export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error("useCall must be used within a CallProvider");
  }
  return context;
};
